import { z } from 'zod';
import { IdSchema } from './horde-event-schema-helpers';
import { HordeIntentPayloadSchemasA } from './horde-intent-schemas-a';
import { HordeIntentPayloadSchemasB } from './horde-intent-schemas-b';
import { HordeIntentPayloadSchemasC } from './horde-intent-schemas-c';
import { HordeStoreContextSchema, PersistedHordeGameSchema, PlayerSecretViewStateSchema } from './horde-session';

export const HordeIntentPayloadSchemas = {
    ...HordeIntentPayloadSchemasA,
    ...HordeIntentPayloadSchemasB,
    ...HordeIntentPayloadSchemasC,
} satisfies Record<string, z.ZodType>;

export type HordeIntent = {
    [Type in keyof typeof HordeIntentPayloadSchemas]: z.infer<(typeof HordeIntentPayloadSchemas)[Type]> & {
        type: Type;
    };
}[keyof typeof HordeIntentPayloadSchemas];

export const HordeIntentSchema = z.unknown().transform((value, context): HordeIntent => {
    if (typeof value !== 'object' || value === null) {
        context.addIssue({ code: 'custom', message: 'Horde intent must be an object.' });

        return z.NEVER;
    }

    const type = Reflect.get(value, 'type');

    if (typeof type !== 'string') {
        context.addIssue({ code: 'custom', message: 'Horde intent requires a string type.' });

        return z.NEVER;
    }

    const schema = HordeIntentPayloadSchemas[type as keyof typeof HordeIntentPayloadSchemas];
    const parsed = schema?.safeParse(value);

    if (!parsed?.success) {
        context.addIssue({
            code: 'custom',
            message: parsed?.error.message ?? `Unknown Horde intent type: ${type}`,
        });

        return z.NEVER;
    }

    return { ...parsed.data, type } as HordeIntent;
});

export const HordeRuntimeEventPayloadSchemas = {
    COMMIT_FAILED: z.object({
        eventId: IdSchema,
        message: z.string().min(1),
    }),
    COMMIT_SUCCEEDED: z.object({
        eventId: IdSchema,
        revision: z.number().int().positive(),
    }),
    CONFLICT_DETECTED: z.object({
        localRevision: z.number().int().nonnegative(),
        remoteRevision: z.number().int().nonnegative(),
    }),
    CONTENT_VERSION_LOADED: z.object({
        contentManifestHash: z.string().min(1),
    }),
    OUTBOUND_INTENT_ACCEPTED: z.object({
        eventId: IdSchema,
        revision: z.number().int().positive(),
    }),
    OUTBOUND_INTENT_QUEUED: z.object({
        eventId: IdSchema,
    }),
    OUTBOUND_INTENT_REJECTED: z.object({
        eventId: IdSchema,
        reason: z.string().min(1),
    }),
    RXDB_PRIVATE_STATE_OBSERVED: z.object({
        privateState: PlayerSecretViewStateSchema.nullable(),
    }),
    RXDB_SNAPSHOT_OBSERVED: z.object({
        snapshot: PersistedHordeGameSchema,
    }),
} satisfies Record<string, z.ZodType>;

export type HordeRuntimeEvent = {
    [Type in keyof typeof HordeRuntimeEventPayloadSchemas]: z.infer<
        (typeof HordeRuntimeEventPayloadSchemas)[Type]
    > & {
        type: Type;
    };
}[keyof typeof HordeRuntimeEventPayloadSchemas];

export const HordeStoreEventPayloadSchemas = {
    ...HordeIntentPayloadSchemas,
    ...HordeRuntimeEventPayloadSchemas,
};

export type HordeStoreEvent = HordeIntent | HordeRuntimeEvent;

export const HordeStoreEmittedPayloadSchemas = {
    HOST_COMMIT_REQUESTED: z.object({
        eventId: IdSchema,
        eventType: z.string().min(1),
        expectedRevision: z.number().int().nonnegative(),
        intent: HordeIntentSchema,
    }),
    JOINER_INTENT_REQUESTED: z.object({
        eventId: IdSchema,
        eventType: z.string().min(1),
        expectedRevision: z.number().int().nonnegative(),
        intent: HordeIntentSchema,
    }),
} satisfies Record<string, z.ZodType>;

export const HordeHydratedContextEventSchema = z.object({
    context: HordeStoreContextSchema,
    type: z.literal('HYDRATED_CONTEXT_READY'),
});
